import { useEffect, useMemo } from 'react';
import Nav from './components/Nav';
import Hero from './components/Hero';
import Quotes from './components/Quotes';
import About from './components/About';
import Music from './components/Music';
import Watch from './components/Watch';
import Live from './components/Live';
import Band from './components/Band';
import Press from './components/Press';
import Support from './components/Support';
import Contact from './components/Contact';
import Footer from './components/Footer';
import { useSheet, useEvents } from './lib/sheets';
import { buildContent } from './lib/content';
import { setAutoLinks } from './lib/autolinks';
import { toEvent } from './lib/events';
import useActiveSection from './lib/useActiveSection';

export default function Site({ initialTarget }) {
  const sheet = useSheet('site');
  const calendar = useEvents();

  const content = useMemo(() => {
    const c = buildContent(sheet.data);
    setAutoLinks(c.autoLinks);           // names that link automatically in sheet text
    return c;
  }, [sheet.data]);

  const events = useMemo(
    () => (calendar.data || []).map((item) => toEvent(item, content.live.keyword)).filter(Boolean),
    [calendar.data, content.live.keyword]
  );

  const loading = sheet.status === 'loading';
  const { sec, art } = content;

  // Which sections are shown (and in the menu). While loading, all of them, so the menu doesn't jump.
  const has = {
    about: true,
    music: loading || content.release.show || content.recordings.length > 0,
    watch: loading || content.videos.length > 0,
    live: true,
    band: loading || content.band.length > 0 || content.recognition.length > 0,
    support: loading || content.support.ways.length > 0 || content.support.mission.length > 0,
    contact: true,
  };
  const visible = (id) => has[id] !== false && sec[id].show;
  const menu = content.navOrder
    .filter((id) => visible(id) && sec[id].label)
    .map((id) => ({ id, label: sec[id].label }));
  const active = useActiveSection(menu.map((m) => m.id));

  // Old page addresses (/Bio, /Calendar ...) and #links: jump once the content has loaded.
  useEffect(() => {
    if (loading) return;
    const target = initialTarget || window.location.hash.slice(1);
    if (!target) return;
    if (initialTarget) window.history.replaceState(null, '', `/#${initialTarget}`);
    const el = document.getElementById(target);
    if (el) el.scrollIntoView();
  }, [loading, initialTarget]);

  return (
    <>
      <a className="skip-link" href="#main">Skip to content</a>
      <Nav sections={menu} active={active} />
      <main id="main">
        <Hero hero={content.hero} />
        <Quotes quotes={content.heroQuotes} />
        {visible('about') && <About about={content.about} art={art.about} loading={loading} />}
        {visible('music') && <Music heading={sec.music.heading} release={content.release} recordings={content.recordings} labels={content.labels} art={art.music} />}
        {visible('watch') && <Watch heading={sec.watch.heading} videos={content.videos} art={art.watch} />}
        {visible('live') && <Live live={content.live} events={events} status={calendar.status} art={art.live} />}
        {visible('band') && <Band heading={sec.band.heading} band={content.band} recognition={content.recognition} labels={content.labels} art={art.band} />}
        {sec.praise.show && <Press heading={sec.praise.heading} quotes={content.praise} art={art.praise} />}
        {visible('support') && <Support heading={sec.support.heading} support={content.support} art={art.support} />}
        {visible('contact') && <Contact contact={content.contact} site={content.site} art={art.contact} />}
      </main>
      <Footer site={content.site} />
    </>
  );
}
